import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Tab,
  Tabs,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import fr from "date-fns/locale/fr";

import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import config from "./global/config";

const RefundManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);

  const [dateRange, setDateRange] = useState({
    start: null,
    end: null,
  });
  const [filters, setFilters] = useState({
    status: "all",
    paymentMethod: "all",
    minAmount: "",
    maxAmount: "",
    searchQuery: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchRefunds = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          status: filters.status !== "all" ? filters.status : "",
          payment_method:
            filters.paymentMethod !== "all" ? filters.paymentMethod : "",
          start_date: dateRange.start
            ? dateRange.start.toISOString().split("T")[0]
            : "",
          end_date: dateRange.end
            ? dateRange.end.toISOString().split("T")[0]
            : "",
          min_amount: filters.minAmount,
          max_amount: filters.maxAmount,
          search: filters.searchQuery,
          tab:
            activeTab === 0
              ? "pending"
              : activeTab === 1
                ? "processed"
                : "rejected",
        });

        const response = await fetch(
          `${config.apiUrl}/refunds_clients.php?${params}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch refunds");
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          setRefunds([]);
          throw new Error("Invalid data format received from server");
        }

        const processedData = data.map((refund) => ({
          id: refund.id_refund,
          ...refund,
          client_nom: refund.client_nom || "",
          client_prenom: refund.client_prenom || "",
          montant_refund: parseFloat(refund.montant_refund) || 0,
          date_refund: refund.date_refund || new Date().toISOString(),
          mode_refund: refund.mode_refund || "",
          statut_refund: refund.statut_refund || "En attente",
        }));

        setRefunds(processedData);
      } catch (error) {
        ////console.error("Error fetching refunds:", error);
        setError(error.message);
        setRefunds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRefunds();
  }, [activeTab, filters, dateRange]);

  const handleProcessRefund = async (refundId, action, data = {}) => {
    try {
      const formData = new FormData();
      formData.append("METHOD", "PUT");
      formData.append("id_refund", refundId);
      formData.append("action", action);
      formData.append("user_upd", localStorage.getItem("username") || "SYSTEM");

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      const response = await fetch(`${config.apiUrl}/refunds_clients.php`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to process refund");
      }

      setRefunds((prevRefunds) =>
        prevRefunds.map((refund) =>
          refund.id_refund === refundId
            ? {
                ...refund,
                statut_refund: action === "approve" ? "Remboursé" : "Rejeté",
              }
            : refund
        )
      );

      setIsProcessModalOpen(false);

      setSnackbar({
        open: true,
        message: `Remboursement ${action === "approve" ? "approuvé" : "rejeté"} avec succès`,
        severity: "success",
      });
    } catch (error) {
      ////console.error("Error processing refund:", error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "numero_vente",
      headerName: "N° Vente",
      width: 130,
    },
    {
      field: "client_nom",
      headerName: "Nom",
      width: 130,
    },
    {
      field: "client_prenom",
      headerName: "Prénom",
      width: 130,
    },
    {
      field: "montant_refund",
      headerName: "Montant",
      width: 130,
    },
    {
      field: "date_refund",
      headerName: "Date",
      width: 130,
    },
    {
      field: "mode_refund",
      headerName: "Mode",
      width: 130,
    },
    {
      field: "statut_refund",
      headerName: "Statut",
      width: 130,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor:
              params.value === "Remboursé"
                ? "success.light"
                : params.value === "Rejeté"
                  ? "error.light"
                  : "warning.light",
            padding: "4px 8px",
            borderRadius: "4px",
            color: "white",
          }}
        >
          {params.value || "En attente"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Détails">
            <IconButton
              onClick={() => {
                setSelectedRefund(params.row);
                setIsDetailsModalOpen(true);
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row?.statut_refund === "En attente" && (
            <Tooltip title="Traiter">
              <IconButton
                onClick={() => {
                  setSelectedRefund(params.row);
                  setIsProcessModalOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Typography variant="h4" mb={3}>
        Gestion des Remboursements
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <Box p={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Rechercher"
                value={filters.searchQuery}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    searchQuery: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filters.status}
                  label="Statut"
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="processed">Remboursé</MenuItem>
                  <MenuItem value="rejected">Rejeté</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Mode de remboursement</InputLabel>
                <Select
                  value={filters.paymentMethod}
                  label="Mode de remboursement"
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="cash">Espèces</MenuItem>
                  <MenuItem value="check">Chèque</MenuItem>
                  <MenuItem value="transfer">Virement</MenuItem>
                  <MenuItem value="card">Carte</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={fr}
              >
                <DatePicker
                  label="Date début"
                  value={dateRange.start}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, start: date }))
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={2}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={fr}
              >
                <DatePicker
                  label="Date fin"
                  value={dateRange.end}
                  onChange={(date) =>
                    setDateRange((prev) => ({ ...prev, end: date }))
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setFilters({
                    status: "all",
                    paymentMethod: "all",
                    minAmount: "",
                    maxAmount: "",
                    searchQuery: "",
                  });
                  setDateRange({ start: null, end: null });
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="En attente" />
          <Tab label="Remboursés" />
          <Tab label="Rejetés" />
        </Tabs>

        <Box height="60vh" width="100%">
          <DataGrid
            rows={refunds}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id_refund}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            sx={{ minHeight: "400px" }}
            initialState={{
              sorting: {
                sortModel: [{ field: "date_refund", sort: "desc" }],
              },
            }}
            components={{
              NoRowsOverlay: () => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Typography color="text.secondary">
                    {loading ? "Chargement..." : "Aucun remboursement trouvé"}
                  </Typography>
                </Box>
              ),
            }}
          />
        </Box>
      </Card>

      {/* Details Modal */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <InfoIcon sx={{ mr: 1 }} /> Détails du remboursement
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRefund && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="subtitle2">N° Vente</Typography>
                <Typography>{selectedRefund.numero_vente}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Client</Typography>
                <Typography>
                  {selectedRefund.client_nom} {selectedRefund.client_prenom}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Montant</Typography>
                <Typography>
                  {selectedRefund.montant_refund.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "MAD",
                  })}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Date</Typography>
                <Typography>
                  {new Date(selectedRefund.date_refund).toLocaleDateString(
                    "fr-FR"
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Mode</Typography>
                <Typography>{selectedRefund.mode_refund}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2">Statut</Typography>
                <Typography>{selectedRefund.statut_refund}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Motif</Typography>
                <Typography>{selectedRefund.motif}</Typography>
              </Grid>
              {selectedRefund.commentaire && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Commentaire</Typography>
                  <Typography>{selectedRefund.commentaire}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDetailsModalOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Process Modal */}
      <Dialog
        open={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} /> Traiter le remboursement
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRefund && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Souhaitez-vous approuver ou rejeter ce remboursement ?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Montant :{" "}
                {selectedRefund.montant_refund.toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "MAD",
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Client : {selectedRefund.client_nom}{" "}
                {selectedRefund.client_prenom}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsProcessModalOpen(false)}>Annuler</Button>
          <Button
            onClick={() =>
              handleProcessRefund(selectedRefund.id_refund, "reject")
            }
            color="error"
            startIcon={<CloseIcon />}
          >
            Rejeter
          </Button>
          <Button
            onClick={() =>
              handleProcessRefund(selectedRefund.id_refund, "approve")
            }
            color="success"
            startIcon={<CheckIcon />}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RefundManagement;
